import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const Liabilities = () => {
  const navigate = useNavigate();
  const [accounts,   setAccounts]   = useState([]);
  const [balances,   setBalances]   = useState({});
  const [loading,    setLoading]    = useState(true);
  const [fyList,     setFyList]     = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);
  const [showModal,  setShowModal]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [form, setForm] = useState({ account_code:"", name:"", description:"", parent_account_id: null });

  // Borrowed capital — what was taken on, and how repayment is going.
  // The sidebar links straight to it, so the tab comes from the URL.
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "borrowings" ? "borrowings" : "accounts");
  const [loans, setLoans] = useState([]);
  const [loanSummary, setLoanSummary] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showBorrow, setShowBorrow] = useState(false);
  const [repaying, setRepaying] = useState(null);
  const [borrowForm, setBorrowForm] = useState({
    lender:"", amount:"", draw_date:new Date().toISOString().split("T")[0],
    due_date:"", interest_rate:"", received_into_account_id:null, notes:"",
  });
  const [repayForm, setRepayForm] = useState({
    amount:"", interest:"", payment_date:new Date().toISOString().split("T")[0],
    paid_from_account_id:null, reference:"", notes:"",
  });

  useEffect(() => { fetchMeta(); fetchLoans(); }, []); // eslint-disable-line
  useEffect(() => { if (selectedFY && accounts.length > 0) fetchBalances(); }, [selectedFY, accounts]);

  const fetchMeta = async () => {
    setLoading(true);
    const [fyRes, accRes] = await Promise.all([
      ajaxAccounting.listFinancialYears({}),
      ajaxAccounting.listAccounts({ type: "liability", postable_only: 0 }),
    ]);
    if (fyRes.status === "OK") {
      const list = fyRes.details || [];
      setFyList(list);
      const cur = list.find((y) => y.is_current === 1) || list[0];
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
    if (accRes.status === "OK") setAccounts(accRes.details || []);
    setLoading(false);
  };

  const fetchBalances = async () => {
    // Fetch balance for each liability account
    const results = {};
    await Promise.all(
      accounts.map(async (acc) => {
        const res = await ajaxAccounting.getAccountBalance({ id: acc.id, financial_year_id: selectedFY?.value });
        if (res.status === "OK") results[acc.id] = res.details?.balance || 0;
      })
    );
    setBalances(results);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.account_code || !form.name) { toast.error("Account code and name required"); return; }
    setSaving(true);
    const res = await ajaxAccounting.createAccount({
      account_code:         form.account_code,
      name:                 form.name,
      type:                 "liability",
      description:          form.description,
      parent_account_id:    form.parent_account_id?.value || null,
      allow_direct_posting: 1,
    });
    if (res.status === "OK") { toast.success("Liability account created!"); fetchMeta(); setShowModal(false); setForm({ account_code:"",name:"",description:"",parent_account_id:null }); }
    else toast.error(res.message);
    setSaving(false);
  };

  const fetchLoans = async () => {
    const [list, summary, banks] = await Promise.all([
      ajaxAccounting.listLoans({}),
      ajaxAccounting.loanSummary(),
      ajaxAccounting.listAccounts({ type: "asset", postable_only: 1 }),
    ]);
    if (list.status === "OK") setLoans(list.details || []);
    if (summary.status === "OK") setLoanSummary(summary.details);
    if (banks.status === "OK") setBankAccounts(banks.details || []);
  };

  const handleBorrow = async (e) => {
    e.preventDefault();
    if (!borrowForm.lender || !(parseFloat(borrowForm.amount) > 0)) {
      toast.error("Enter who the money is from and how much"); return;
    }
    if (!selectedFY) { toast.error("Select a financial year"); return; }
    setSaving(true);
    const res = await ajaxAccounting.borrowCapital({
      lender: borrowForm.lender,
      amount: parseFloat(borrowForm.amount),
      draw_date: borrowForm.draw_date,
      due_date: borrowForm.due_date || null,
      interest_rate: parseFloat(borrowForm.interest_rate) || 0,
      received_into_account_id: borrowForm.received_into_account_id?.value || null,
      notes: borrowForm.notes,
      financial_year_id: selectedFY.value,
    });
    setSaving(false);
    if (res.status === "OK") {
      toast.success(res.message || "Borrowing recorded");
      setShowBorrow(false);
      setBorrowForm({ lender:"", amount:"", draw_date:new Date().toISOString().split("T")[0],
        due_date:"", interest_rate:"", received_into_account_id:null, notes:"" });
      fetchLoans(); fetchBalances();
    } else toast.error(res.message);
  };

  const handleRepay = async (e) => {
    e.preventDefault();
    if (!(parseFloat(repayForm.amount) > 0)) { toast.error("Enter the amount repaid"); return; }
    if (!selectedFY) { toast.error("Select a financial year"); return; }
    setSaving(true);
    const res = await ajaxAccounting.repayLoan({
      loan_id: repaying.id,
      amount: parseFloat(repayForm.amount),
      interest: parseFloat(repayForm.interest) || 0,
      payment_date: repayForm.payment_date,
      paid_from_account_id: repayForm.paid_from_account_id?.value || null,
      reference: repayForm.reference || null,
      notes: repayForm.notes,
      financial_year_id: selectedFY.value,
    });
    setSaving(false);
    if (res.status === "OK") {
      toast.success(res.message || "Repayment recorded");
      setRepaying(null);
      setRepayForm({ amount:"", interest:"", payment_date:new Date().toISOString().split("T")[0],
        paid_from_account_id:null, reference:"", notes:"" });
      fetchLoans(); fetchBalances();
    } else toast.error(res.message);
  };

  const bankOptions = bankAccounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));

  const loanColumns = [
    { key: "reference", label: "Reference", render: (v) => <code className="tx-12">{v}</code> },
    { key: "lender", label: "Lender", render: (v) => <strong>{v}</strong> },
    { key: "principal", label: "Borrowed", numeric: true, render: (v) => functions.formatCurrency(v) },
    { key: "repaid", label: "Repaid", numeric: true, render: (v) => <span className="tx-success">{functions.formatCurrency(v)}</span> },
    { key: "balance", label: "Outstanding", numeric: true,
      render: (v) => <strong className={parseFloat(v) > 0 ? "tx-danger" : "tx-muted"}>{functions.formatCurrency(v)}</strong> },
    { key: "repaid_pct", label: "Progress", sortValue: (r) => parseFloat(r.repaid_pct) || 0,
      render: (v) => (
        <div style={{ minWidth: 90 }}>
          <div className="progress" style={{ height: 6 }}>
            <div className="progress-bar bg-success" style={{ width: `${Math.min(100, parseFloat(v) || 0)}%` }} />
          </div>
          <span className="tx-11 tx-muted">{parseFloat(v) || 0}%</span>
        </div>
      ) },
    { key: "due_date", label: "Due", render: (v, r) => v
        ? <span className={r.overdue ? "tx-danger fw-semibold" : ""}>{functions.formatDate(v)}</span>
        : <span className="tx-muted">—</span> },
    { key: "status", label: "Status", render: (v, r) => (
        <span className={`badge ${r.overdue ? "bg-danger" : v === "settled" ? "bg-success" : "bg-primary"}`}>
          {r.overdue ? "Overdue" : v}
        </span>
      ) },
    { key: "id", label: "Actions", render: (id, row) => (
        row.status === "open" ? (
          <button className="btn btn-sm btn-outline-primary" onClick={() => setRepaying(row)}>
            <i className="fa-solid fa-hand-holding-dollar me-1" />Repay
          </button>
        ) : <span className="tx-muted tx-12">Settled</span>
      ) },
  ];

  const parentOptions = accounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.name}` }));
  const fyOptions = fyList.map((y) => ({ value: y.id, label: y.name }));

  const totalLiabilities = accounts.reduce((s, acc) => s + Math.abs(balances[acc.id] || 0), 0);

  // Group by parent
  const grouped = {};
  accounts.forEach((acc) => {
    const group = acc.parent_account_id ? (accounts.find((a) => a.id === acc.parent_account_id)?.name || "Other") : "Top Level";
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(acc);
  });

  const columns = [
    { key: "account_code", label: "Code",    render: (v) => <code>{v}</code> },
    { key: "name",          label: "Account", render: (v) => <strong>{v}</strong> },
    { key: "description",   label: "Description", render: (v) => <span className="tx-12">{v || "—"}</span> },
    { key: "id",            label: "Balance", render: (id) => {
      const bal = balances[id];
      return bal !== undefined ? <span className={`fw-bold ${Math.abs(bal) > 0 ? "tx-danger" : "tx-muted"}`}>{functions.formatCurrency(Math.abs(bal))}</span> : <span className="tx-muted tx-11">—</span>;
    }},
    { key: "is_active", label: "Status", render: (v) => v !== 0 ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span> },
    { key: "id", label: "Actions", render: (id, row) => (
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/ledger?account_id=${id}`)} title="View Ledger">
          <i className="fa-solid fa-table-list"></i>
        </button>
        <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/accounting/journal/new?type=general`)} title="Post Entry">
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      </div>
    )},
  ];

  return (
    <AppContainer
      title="Liabilities Tracker"
      previousPageTitle="Assets & Liabilities"
      description="Track all liability accounts — loans, payables, accruals, and deferred income. Balances are pulled from the ledger for the selected financial year. Post journal entries to record new liabilities or repayments."
      actions={[
        { label: "Add Liability Account", icon: "fa-solid fa-plus",        className: "btn btn-primary",          onClick: () => setShowModal(true) },
        { label: "New Journal Entry",      icon: "fa-solid fa-book",        className: "btn btn-outline-secondary", onClick: () => navigate("/accounting/journal/new?type=general") },
      ]}
    >
      <Toaster />

      <div className="card custom-card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select year" isSearchable />
          </div>
          <div className="col-lg-8">
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <div className="card p-2 border-0 bg-light" style={{ borderLeft: "3px solid #ef4444", minWidth: "180px" }}>
                <div className="tx-11 tx-muted">Total Liabilities</div>
                <div className="fw-bold tx-danger tx-16">{functions.formatCurrency(totalLiabilities)}</div>
              </div>
              <div className="card p-2 border-0 bg-light">
                <div className="tx-11 tx-muted">Liability Accounts</div>
                <div className="fw-bold tx-16">{accounts.length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        {[
          { key: "accounts",   label: "Liability Accounts", icon: "fa-scale-unbalanced" },
          { key: "borrowings", label: "Borrowed Capital",   icon: "fa-hand-holding-dollar" },
        ].map((t) => (
          <li className="nav-item" key={t.key}>
            <button className={`nav-link ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
              <i className={`fa-solid ${t.icon} me-1`} />{t.label}
            </button>
          </li>
        ))}
      </ul>

      {tab === "borrowings" && (
        <>
          {loanSummary && (
            <div className="row g-2 mb-3">
              {[
                { label: "Borrowed", value: loanSummary.borrowed },
                { label: "Repaid", value: loanSummary.repaid, tone: "tx-success" },
                { label: "Still Owed", value: loanSummary.outstanding, tone: "tx-danger" },
                { label: "Interest Paid", value: loanSummary.interest_paid },
              ].map((c) => (
                <div className="col-lg-3 col-6" key={c.label}>
                  <div className="card custom-card p-3 h-100">
                    <div className="tx-11 tx-muted text-uppercase">{c.label}</div>
                    <div className={`fw-bold tx-16 ${c.tone || ""}`}>{functions.formatCurrency(c.value)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loanSummary?.overdue_loans > 0 && (
            <div className="alert alert-danger py-2 tx-13">
              <i className="fa-solid fa-triangle-exclamation me-1" />
              {loanSummary.overdue_loans} borrowing{loanSummary.overdue_loans === 1 ? " is" : "s are"} past
              the agreed repayment date.
            </div>
          )}

          <div className="card custom-card p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-semibold mb-0">Borrowed Capital</h6>
              <button className="btn btn-sm btn-primary" onClick={() => setShowBorrow(true)}>
                <i className="fa-solid fa-plus me-1" />Record Borrowing
              </button>
            </div>
            <p className="tx-12 tx-muted">
              Borrowing raises cash and an equal obligation. A repayment returns capital;
              any interest is recorded separately as a cost of borrowing.
            </p>
            <DataTable
              columns={loanColumns}
              data={loans}
              pageSize={15}
              showRowNumbers
              emptyMessage="No borrowings recorded yet."
            />
          </div>
        </>
      )}

      {tab === "accounts" && (
      <div className="card custom-card p-3">
        <ExportBar
          data={accounts.map((a) => ({ ...a, balance: balances[a.id] || 0 }))}
          columns={[{key:"account_code",label:"Code"},{key:"name",label:"Account"},{key:"description",label:"Description"},{key:"balance",label:"Balance"}]}
          filename="liabilities" pdfTitle="Liabilities Report"
          printCode="ACC-LIABILITIES" exportCode="ACC-LIABILITIES"
        />
        <DataTable columns={columns} data={accounts} loading={loading} pageSize={15}
          emptyMessage="No liability accounts found. Add some from Chart of Accounts or use the button above." />
      </div>
      )}

      {/* RECORD BORROWING */}
      {showBorrow && (
        <AppModal
          title="Record borrowed capital"
          icon="fa-solid fa-hand-holding-dollar"
          subtitle="Raises the cash received and the obligation it creates."
          onClose={() => setShowBorrow(false)}
          onSubmit={handleBorrow}
          busy={saving}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setShowBorrow(false)} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Record Borrowing"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Borrowed from *</label>
              <input className="form-control" placeholder="Bank, director, SACCO…" value={borrowForm.lender}
                onChange={(e) => setBorrowForm({ ...borrowForm, lender: e.target.value })} required />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Amount *</label>
              <input type="number" min="0" step="0.01" className="form-control" placeholder="0.00"
                value={borrowForm.amount} onChange={(e) => setBorrowForm({ ...borrowForm, amount: e.target.value })} required />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Date received</label>
              <input type="date" className="form-control" value={borrowForm.draw_date}
                onChange={(e) => setBorrowForm({ ...borrowForm, draw_date: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Repayable by</label>
              <input type="date" className="form-control" value={borrowForm.due_date}
                onChange={(e) => setBorrowForm({ ...borrowForm, due_date: e.target.value })} />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Interest rate (%)</label>
              <input type="number" min="0" step="0.01" className="form-control" placeholder="e.g. 12.5"
                value={borrowForm.interest_rate} onChange={(e) => setBorrowForm({ ...borrowForm, interest_rate: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Received into</label>
              <Select options={bankOptions} value={borrowForm.received_into_account_id}
                onChange={(v) => setBorrowForm({ ...borrowForm, received_into_account_id: v })}
                placeholder="Default bank account" isClearable
                menuPortalTarget={document.body} styles={{ menuPortal: (b) => ({ ...b, zIndex: 2000 }) }} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Notes</label>
              <input className="form-control" value={borrowForm.notes}
                onChange={(e) => setBorrowForm({ ...borrowForm, notes: e.target.value })} />
            </div>
          </div>
        </AppModal>
      )}

      {/* RECORD REPAYMENT */}
      {repaying && (
        <AppModal
          title={`Repay ${repaying.lender}`}
          icon="fa-solid fa-money-bill-transfer"
          subtitle={`Outstanding: ${functions.formatCurrency(repaying.balance)}`}
          onClose={() => setRepaying(null)}
          onSubmit={handleRepay}
          busy={saving}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setRepaying(null)} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Record Repayment"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Amount paid *</label>
              <input type="number" min="0" step="0.01" className="form-control" placeholder="0.00"
                value={repayForm.amount} onChange={(e) => setRepayForm({ ...repayForm, amount: e.target.value })} required />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Of which interest</label>
              <input type="number" min="0" step="0.01" className="form-control" placeholder="0.00"
                value={repayForm.interest} onChange={(e) => setRepayForm({ ...repayForm, interest: e.target.value })} />
              <small className="tx-muted tx-11">Interest is a cost, so only the rest reduces what is owed.</small>
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Payment date</label>
              <input type="date" className="form-control" value={repayForm.payment_date}
                onChange={(e) => setRepayForm({ ...repayForm, payment_date: e.target.value })} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Paid from</label>
              <Select options={bankOptions} value={repayForm.paid_from_account_id}
                onChange={(v) => setRepayForm({ ...repayForm, paid_from_account_id: v })}
                placeholder="Default bank account" isClearable
                menuPortalTarget={document.body} styles={{ menuPortal: (b) => ({ ...b, zIndex: 2000 }) }} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Reference</label>
              <input className="form-control" placeholder="Cheque / transfer reference"
                value={repayForm.reference} onChange={(e) => setRepayForm({ ...repayForm, reference: e.target.value })} />
            </div>
          </div>

          {parseFloat(repayForm.amount) > 0 && (
            <div className="alert alert-light py-2 tx-13 mt-3 mb-0 border">
              Capital repaid:{" "}
              <strong>
                {functions.formatCurrency(
                  Math.max(0, (parseFloat(repayForm.amount) || 0) - (parseFloat(repayForm.interest) || 0)),
                )}
              </strong>
              {" · "}Remaining after this:{" "}
              <strong>
                {functions.formatCurrency(
                  Math.max(0, parseFloat(repaying.balance) -
                    ((parseFloat(repayForm.amount) || 0) - (parseFloat(repayForm.interest) || 0))),
                )}
              </strong>
            </div>
          )}
        </AppModal>
      )}

      {/* ADD MODAL */}
      {showModal && (
        <AppModal
          title="Add Liability Account"
          onClose={() => setShowModal(false)}
          onSubmit={handleCreate}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Add Account"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Account Code <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.account_code}
                onChange={(e) => setForm({...form,account_code:e.target.value})} placeholder="e.g. 2100" required />
            </div>
            <div className="col-lg-8">
              <label className="form-label tx-12 fw-semibold">Account Name <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.name}
                onChange={(e) => setForm({...form,name:e.target.value})} placeholder="e.g. Bank Loan — Stanbic" required />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Parent Account</label>
              <Select options={parentOptions} value={form.parent_account_id}
                onChange={(v) => setForm({...form,parent_account_id:v})} placeholder="No parent (top-level)" isClearable isSearchable />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Description</label>
              <textarea className="form-control" rows={2} value={form.description}
                onChange={(e) => setForm({...form,description:e.target.value})} />
            </div>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default Liabilities;
